"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.UnavailabilityInfobox = void 0;
const ui_1 = require("@app/component/ui");
const graphql_1 = require("@app/gql-types/graphql");
const request_1 = require("@app/request");
const store_1 = require("@app/store");
const util_1 = require("@app/util");
const error_handler_1 = require("@app/util/error-handler");
const format_hours_1 = require("@app/util/format-hours");
const maybe_1 = require("@app/util/functional/maybe");
const use_workspace_unavailability_categories_1 = require("@app/util/hooks/use-workspace-unavailability-categories");
const useWorkspace_1 = require("@app/util/hooks/useWorkspace");
const sentry_error_1 = require("@app/util/sentry/sentry-error");
const moment_1 = __importDefault(require("moment"));
const react_1 = __importDefault(require("react"));
const react_hook_form_1 = require("react-hook-form");
const unav_overtime_stats_1 = require("../requests/unav-overtime-stats");
const unavailability_context_1 = require("../unavailability-context");
const use_get_shifts_work_minutes_1 = require("../util/use-get-shifts-work-minutes");
const loadUserRoleStats = async (userId, periodStart, workspaceId) => {
    const referenceDate = periodStart;
    if (!userId) {
        return undefined;
    }
    const res = await (0, request_1.requestRoleStats)({
        workspace: workspaceId,
        user: userId,
        referenceDate: (0, moment_1.default)(referenceDate),
    });
    if ((0, error_handler_1.isError)(res)) {
        return undefined;
    }
    return res;
};
const getRemainingLabel = (unavDaysInYear, displayAsDays) => {
    const x = unavDaysInYear?.minutesRemaining;
    const y = unavDaysInYear?.minutesMaximum;
    if (!(0, util_1.isValue)(x) || !(0, util_1.isValue)(y)) {
        return (0, util_1.t)('UNAV_REMAINING', {
            x: '?',
            y: '?',
        });
    }
    if (displayAsDays) {
        return ((0, util_1.t)('REMAINING') +
            ': ' +
            (0, util_1.t)('REQUEST_UNAV_REMAINING_XY_DAYS', {
                x: util_1.numberUtil.round2decimals(x / 480).toString(),
                y: util_1.numberUtil.round2decimals(y / 480).toString(),
            }).toLowerCase());
    }
    return (0, util_1.t)('UNAV_REMAINING', {
        x: util_1.timeUtil.asHour(x),
        y: util_1.timeUtil.asHour(y),
    });
};
const UnavailabilityInfobox = () => {
    const [userRoleStats, setUserRoleStats] = react_1.default.useState();
    const { formControl, initialData, isEditing } = (0, unavailability_context_1.useUnavailability)();
    const workMinutesInclusionOptions = react_1.default.useMemo(() => [
        {
            label: (0, util_1.t)('DAYS_WITH_PLANNED_SHIFT'),
            value: graphql_1.TimeOffWorkMinutesInclusion.ShiftDays,
        },
        {
            label: (0, util_1.t)('EVERY_WORK_DAY'),
            value: graphql_1.TimeOffWorkMinutesInclusion.WorkDays,
        },
        {
            label: (0, util_1.t)('EVERY_DAY'),
            value: graphql_1.TimeOffWorkMinutesInclusion.EveryDay,
        },
    ], []);
    const workMinutesByOptions = react_1.default.useMemo(() => [
        util_1.miscUtil.getFilteredContentForOrganization('time_off_edit_include_hour_option_contract', {
            label: (0, util_1.t)('BY_CONTRACT'),
            value: graphql_1.TimeOffWorkMinutesBy.Contract,
        }),
        {
            label: (0, util_1.t)('BY_REPLACED_SHIFT'),
            value: graphql_1.TimeOffWorkMinutesBy.Shift,
        },
        {
            label: (0, util_1.t)('CUSTOM'),
            value: graphql_1.TimeOffWorkMinutesBy.Custom,
        },
    ].filter(util_1.isValue), []);
    const workMinutesTotalShifts = (0, use_get_shifts_work_minutes_1.useGetShiftsWorkMinutes)();
    const _period = (0, react_hook_form_1.useWatch)({
        control: formControl,
        name: 'period',
    });
    const _periodStart = _period?.start;
    const _workMinutesInclusion = (0, react_hook_form_1.useWatch)({
        control: formControl,
        name: 'workMinutesInclusion',
    });
    const _workMinutesBy = (0, react_hook_form_1.useWatch)({
        control: formControl,
        name: 'workMinutesBy',
    });
    const _workMinutes = (0, react_hook_form_1.useWatch)({
        control: formControl,
        name: 'workMinutes',
    });
    const _workDays = initialData?.workDays;
    const _userId = (0, react_hook_form_1.useWatch)({ control: formControl, name: 'user' });
    const _contractId = (0, react_hook_form_1.useWatch)({ control: formControl, name: 'contractId' });
    const _selectedCategoryId = (0, react_hook_form_1.useWatch)({ control: formControl, name: 'categoryId' });
    const workspace = (0, useWorkspace_1.useWorkspace)();
    react_1.default.useEffect(() => {
        const workspaceId = workspace?.id;
        if (!(0, util_1.isValue)(_userId))
            return;
        if (!(0, util_1.isValue)(_periodStart))
            return;
        if (!(0, util_1.isValue)(workspaceId))
            return;
        void loadUserRoleStats(_userId, _periodStart, workspaceId).then((res) => {
            setUserRoleStats(res);
        });
    }, [_userId, _periodStart, setUserRoleStats]);
    const unavCategories = (0, use_workspace_unavailability_categories_1.getWorkspaceUnavailabilityCategories)(workspace);
    const selectedCategory = unavCategories.find((con) => con.id === _selectedCategoryId);
    const displayAsDays = selectedCategory?.displayWorkTimeAsDays;
    const employees = (0, store_1.useRootSelector)((state) => state.employees);
    const selectedEmployee = (0, util_1.isValue)(_userId) ? employees[_userId] : undefined;
    const desiredContract = selectedEmployee?.contracts?.find((con) => con.id === _contractId);
    const hoursOfWorkPerShift = (desiredContract?.options?.minutesOfWorkPerShift ?? 480) / 60;
    const _workDaysBackup = (0, util_1.isValue)(_workMinutes)
        ? _workMinutes / 60 / hoursOfWorkPerShift
        : 0;
    const decideCounted = () => {
        const contractOptions = desiredContract?.options;
        const contractAmount = contractOptions?.minutesOfWorkPerShift / 60 || 0;
        if (_workMinutesBy === graphql_1.TimeOffWorkMinutesBy.Settings) {
            if (selectedCategory?.maxDailyMinutes) {
                return selectedCategory?.maxDailyMinutes / 60;
            }
            else {
                return contractAmount;
            }
        }
        if (_workMinutesBy === graphql_1.TimeOffWorkMinutesBy.Contract) {
            return contractAmount;
        }
        if (_workMinutesBy === graphql_1.TimeOffWorkMinutesBy.Shift) {
            if (workMinutesTotalShifts === 0)
                return (_workMinutes ?? 0) / 60;
            return workMinutesTotalShifts / 60;
        }
        return (_workMinutes ?? 0) / 60;
    };
    const workMinutesInclusionLabel = new maybe_1.Maybe(workMinutesInclusionOptions.find((opt) => opt?.value === _workMinutesInclusion)
        ?.label)
        .map((_label) => `${_label} ${(0, util_1.t)('AND')} `)
        .get(() => '');
    const workMinutesByLabel = workMinutesByOptions.find((opt) => opt?.value === _workMinutesBy)?.label;
    const showNoValue = !isEditing && _workMinutesBy === graphql_1.TimeOffWorkMinutesBy.Shift;
    const workMinutesLabel = new maybe_1.Maybe(decideCounted())
        .map((_wm) => showNoValue
        ? ' - ?'
        : ` - ${displayAsDays ? (0, util_1.t)('DAYS_X', { x: _workDays ?? util_1.numberUtil.round2decimals(_workDaysBackup, false) }) : (0, format_hours_1.formatHours)(_wm)}`)
        .get(() => '');
    const totalInclusionLabel = `${workMinutesInclusionLabel}${workMinutesByLabel?.toLowerCase() ?? ''}${workMinutesLabel}`;
    const unavDaysInYear = userRoleStats?.yearTimeOffs?.find((u) => u?.categoryId === selectedCategory?.id);
    const shouldUseOvertimeFund = selectedCategory?.timeInLieu;
    const [currentPendingTimeOffInLieuMinutes, setCurrentPendingTimeOffInLieuMinutes] = react_1.default.useState(0);
    react_1.default.useEffect(() => {
        const action = async () => {
            if (!shouldUseOvertimeFund)
                return;
            const workspaceId = workspace?.id;
            if (!(0, util_1.isDefined)(workspaceId)) {
                throw new sentry_error_1.SentryError('unavailability-infobox: workspaceId cannot be undefined');
            }
            const res = await (0, unav_overtime_stats_1.requestUnavOvertimeStats)({
                workspaceId,
                period: {
                    start: (0, moment_1.default)().subtract(3, 'months').startOf('month'),
                    end: (0, moment_1.default)().endOf('month'),
                },
            });
            if ((0, util_1.isUndefined)(res))
                return;
            const overTimeFundEmployee = res.find((stat) => stat.userId === _userId)?.currentPendingTimeOffInLieuMinutes;
            if ((0, util_1.isDefined)(overTimeFundEmployee)) {
                setCurrentPendingTimeOffInLieuMinutes(overTimeFundEmployee);
            }
        };
        action();
    }, [shouldUseOvertimeFund, _userId]);
    const overtimeFundDays = util_1.numberUtil.round2decimals(currentPendingTimeOffInLieuMinutes / 60 / 8, false);
    const overtimeFundHours = (0, format_hours_1.formatHours)(currentPendingTimeOffInLieuMinutes / 60);
    const remainingLabel = getRemainingLabel(unavDaysInYear, displayAsDays);
    // console.log('[UnavailabilityInfobox] overtime fund label calculation', {
    //   shouldUseOvertimeFund,
    //   displayAsDays,
    //   currentPendingTimeOffInLieuMinutes,
    //   overtimeFundDays,
    //   overtimeFundHours,
    //   remainingLabel,
    //   unavDaysInYear,
    // })
    const overtimeFundLabel = shouldUseOvertimeFund
        ? displayAsDays
            ? (0, util_1.t)('DAYS_X', { x: overtimeFundDays })
            : overtimeFundHours
        : remainingLabel;
    return (react_1.default.createElement("div", { style: {
            width: '100%',
            position: 'sticky',
            paddingTop: '0.5rem',
            paddingBottom: '0.25rem',
            top: 0,
            zIndex: 10,
            background: 'white',
        } },
        react_1.default.createElement(ui_1.Alert, { style: {
                height: '3.25rem',
                width: '100%',
                paddingTop: '0.6rem',
                display: 'flex',
                alignItems: 'start',
            }, size: 'FULL_WIDTH' },
            react_1.default.createElement("div", { style: {
                    width: '100%',
                    height: 'calc(100% + 0.2rem)',
                    display: 'flex',
                    alignItems: 'start',
                    justifyContent: 'space-between',
                    paddingInline: '0.5rem',
                } },
                react_1.default.createElement("div", { style: {
                        flexGrow: 0,
                        flexShrink: 0,
                        justifyContent: 'start',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.25rem',
                    } },
                    react_1.default.createElement(ui_1.Text, { weight: 'medium', color: 'primary', type: 'body-large' }, `${(0, util_1.t)('INCLUSION')}:`),
                    react_1.default.createElement(ui_1.Text, { weight: 'medium', color: 'primary', type: 'body-large' }, totalInclusionLabel)),
                react_1.default.createElement("div", { style: {
                        flexGrow: 0,
                        flexShrink: 0,
                        justifyContent: 'end',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.25rem',
                    } },
                    react_1.default.createElement(ui_1.Text, { weight: 'medium', color: 'primary', type: 'body-large' },
                        (0, util_1.t)('USED_VACATION_DAYS_LABEL'),
                        ":",
                        ' ',
                        showNoValue
                            ? '?'
                            : displayAsDays
                                ? (0, util_1.t)('DAYS_X', {
                                    x: _workDays ??
                                        util_1.numberUtil.round2decimals(_workDaysBackup, false),
                                })
                                : (0, format_hours_1.formatHours)(decideCounted())),
                    react_1.default.createElement(ui_1.Text, { weight: 'medium', color: 'primary', type: 'body-large' },
                        (0, util_1.t)('FOND'),
                        " ",
                        (0, util_1.t)('HOURS_COUNT'),
                        ": ",
                        overtimeFundLabel))))));
};
exports.UnavailabilityInfobox = UnavailabilityInfobox;
